import { createFileRoute } from '@tanstack/react-router';
import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Checkbox,
  Group,
  Modal,
  NumberInput,
  SegmentedControl,
  Select,
  Skeleton,
  Stack,
  Switch,
  Table,
  Text,
  Textarea,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconCirclePlus, IconCopy, IconPencil, IconRobot, IconTrash } from '@tabler/icons-react';
import { motion } from 'framer-motion';
import { useState } from 'react';
import { notifications } from '@mantine/notifications';
import { trpc } from '../../lib/trpc';
import { PermissionGate } from '../../components/PermissionGate';

const TRIGGER_TYPES = [
  'welcome',
  'is',
  'starts_with',
  'ends_with',
  'contains_word',
  'contains',
  'stop_promotional',
  'start_promotional',
  'start_ai_bot',
  'stop_ai_bot',
] as const;

const CHANNELS = [
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'messenger', label: 'Messenger' },
  { value: 'instagram', label: 'Instagram' },
] as const;
type ReplyChannel = (typeof CHANNELS)[number]['value'];

const channelLabel = (v: string) => CHANNELS.find((c) => c.value === v)?.label ?? v;

type ReplyForm = {
  name: string;
  triggerType: (typeof TRIGGER_TYPES)[number];
  triggerSubject: string;
  replyText: string;
  replyKind: 'text' | 'flow';
  flowUid: string;
  priorityIndex: number;
  channel: ReplyChannel;
  igScope: 'dm' | 'comment' | 'story_reply';
};

const blankForm = (channel: ReplyChannel): ReplyForm => ({
  name: '',
  triggerType: 'is',
  triggerSubject: '',
  replyText: '',
  replyKind: 'text',
  flowUid: '',
  priorityIndex: 0,
  channel,
  igScope: 'dm',
});

function BotRepliesPage() {
  const [viewChannel, setViewChannel] = useState<ReplyChannel>('whatsapp');
  const list = trpc.bots.replies.list.useQuery({ channel: viewChannel });
  type ReplyRow = NonNullable<typeof list.data>[number];
  const utils = trpc.useUtils();
  const invalidate = () => void utils.bots.replies.list.invalidate();
  const create = trpc.bots.replies.create.useMutation({
    onSuccess: () => {
      invalidate();
      notifications.show({ message: 'Bot reply created', color: 'green' });
    },
    onError: (e) => notifications.show({ message: e.message, color: 'red' }),
  });
  const update = trpc.bots.replies.update.useMutation({
    onSuccess: () => {
      invalidate();
      notifications.show({ message: 'Bot reply updated', color: 'green' });
    },
    onError: (e) => notifications.show({ message: e.message, color: 'red' }),
  });
  const duplicate = trpc.bots.replies.duplicate.useMutation({
    onSuccess: (r) => {
      invalidate();
      setDupSource(null);
      notifications.show({
        message: `Duplicated to ${r.ids.length} channel${r.ids.length === 1 ? '' : 's'}`,
        color: 'green',
      });
    },
    onError: (e) => notifications.show({ message: e.message, color: 'red' }),
  });
  const setStatus = trpc.bots.replies.setStatus.useMutation({
    onSuccess: () => invalidate(),
  });
  const del = trpc.bots.replies.delete.useMutation({
    onSuccess: () => invalidate(),
  });

  const [opened, { open, close }] = useDisclosure(false);
  const publishedForms = trpc.forms.list.useQuery();
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<ReplyForm>(blankForm('whatsapp'));

  // Duplicate-to-channels modal.
  const [dupSource, setDupSource] = useState<ReplyRow | null>(null);
  const [dupChannels, setDupChannels] = useState<ReplyChannel[]>([]);

  const openCreate = () => {
    // Default the new reply to the channel currently being viewed.
    setEditingId(null);
    setForm(blankForm(viewChannel));
    open();
  };

  const openEdit = (b: ReplyRow) => {
    let flowUid = '';
    if (b.messageType === 'flow' && b.dataJson) {
      try {
        flowUid = (JSON.parse(b.dataJson) as { flowUid?: string }).flowUid ?? '';
      } catch {
        flowUid = '';
      }
    }
    setEditingId(b.id);
    setForm({
      name: b.name,
      triggerType: (b.triggerType as (typeof TRIGGER_TYPES)[number]) ?? 'is',
      triggerSubject: b.triggerSubject ?? '',
      replyText: b.replyText ?? '',
      replyKind: b.messageType === 'flow' ? 'flow' : 'text',
      flowUid,
      priorityIndex: b.priorityIndex,
      channel: (b.channel as ReplyChannel) ?? 'whatsapp',
      igScope: (b.igScope as 'dm' | 'comment' | 'story_reply') ?? 'dm',
    });
    open();
  };

  const openDup = (b: ReplyRow) => {
    setDupSource(b);
    setDupChannels(CHANNELS.map((c) => c.value).filter((v) => v !== b.channel));
  };

  const submit = () => {
    if (!form.name.trim()) return;
    if (form.replyKind === 'flow' && !form.flowUid) return;
    const isFlow = form.replyKind === 'flow';
    const payload = {
      name: form.name,
      triggerType: form.triggerType,
      triggerSubject: form.triggerSubject || null,
      messageType: (isFlow ? 'flow' : 'simple') as 'flow' | 'simple',
      replyText: isFlow ? null : form.replyText || null,
      dataJson: isFlow ? JSON.stringify({ flowUid: form.flowUid }) : null,
      status: 1,
      channel: form.channel,
      ...(form.channel === 'instagram' ? { igScope: form.igScope } : {}),
      priorityIndex: form.priorityIndex,
    };
    if (editingId != null) update.mutate({ id: editingId, ...payload });
    else create.mutate(payload);
    close();
    setEditingId(null);
    setForm(blankForm(viewChannel));
  };

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <Stack gap="md">
        <Group justify="space-between">
          <Title order={2}>Auto-replies</Title>
          <Button leftSection={<IconCirclePlus size={14} />} onClick={openCreate}>
            New reply
          </Button>
        </Group>

        <SegmentedControl
          value={viewChannel}
          onChange={(v) => setViewChannel(v as ReplyChannel)}
          data={CHANNELS.map((c) => ({ value: c.value, label: c.label }))}
        />

        <Card p={0}>
          {list.isPending ? (
            <Skeleton height={200} m="md" />
          ) : (
            <Table striped highlightOnHover withRowBorders={false}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Name</Table.Th>
                  <Table.Th>Trigger</Table.Th>
                  <Table.Th>Subject</Table.Th>
                  <Table.Th>Priority</Table.Th>
                  <Table.Th>Hits</Table.Th>
                  <Table.Th>Active</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {(list.data ?? []).map((b) => (
                  <Table.Tr key={b.id}>
                    <Table.Td>
                      <Text fw={500}>{b.name}</Text>
                      <Text c="dimmed" size="xs" lineClamp={1}>
                        {b.replyText ?? '—'}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge variant="light" size="xs">
                        {b.triggerType}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text ff="monospace" size="sm">
                        {b.triggerSubject ?? '—'}
                      </Text>
                    </Table.Td>
                    <Table.Td>{b.priorityIndex}</Table.Td>
                    <Table.Td>
                      <Text size="sm" c={b.hitCount === 0 ? 'red' : undefined}>
                        {String(b.hitCount)}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Switch
                        checked={b.status === 1}
                        onChange={(e) =>
                          setStatus.mutate({
                            id: b.id,
                            status: e.currentTarget.checked ? '1' : '2',
                          })
                        }
                      />
                    </Table.Td>
                    <Table.Td align="right">
                      <Group gap={4} justify="end" wrap="nowrap">
                        <Tooltip label="Edit">
                          <ActionIcon
                            variant="subtle"
                            onClick={() => openEdit(b)}
                            aria-label="Edit"
                          >
                            <IconPencil size={14} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Duplicate to other channels">
                          <ActionIcon
                            variant="subtle"
                            onClick={() => openDup(b)}
                            aria-label="Duplicate"
                          >
                            <IconCopy size={14} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Delete">
                          <ActionIcon
                            color="red"
                            variant="subtle"
                            onClick={() => del.mutate({ id: b.id })}
                            aria-label="Delete"
                          >
                            <IconTrash size={14} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
                {(list.data ?? []).length === 0 && (
                  <Table.Tr>
                    <Table.Td colSpan={7}>
                      <Text c="dimmed" ta="center" py="md">
                        No bot replies yet.
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          )}
        </Card>

        <Modal
          opened={opened}
          onClose={close}
          title={editingId != null ? 'Edit bot reply' : 'New bot reply'}
          centered
          size="lg"
        >
          <Stack gap="sm">
            <TextInput
              label="Name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.currentTarget.value })}
            />
            <Group grow>
              <Select
                label="Channel"
                data={CHANNELS.map((c) => ({ value: c.value, label: c.label }))}
                value={form.channel}
                onChange={(v) =>
                  setForm({
                    ...form,
                    channel: (v as ReplyChannel) ?? 'whatsapp',
                    // WhatsApp Flow forms are WA-only — fall back to text on FB/IG.
                    replyKind: v === 'whatsapp' ? form.replyKind : 'text',
                  })
                }
              />
              {form.channel === 'instagram' && (
                <Select
                  label="Instagram scope"
                  data={[
                    { value: 'dm', label: 'Direct message' },
                    { value: 'comment', label: 'Comment' },
                    { value: 'story_reply', label: 'Story reply' },
                  ]}
                  value={form.igScope}
                  onChange={(v) =>
                    setForm({ ...form, igScope: (v as 'dm' | 'comment' | 'story_reply') ?? 'dm' })
                  }
                />
              )}
            </Group>
            <Group grow>
              <Select
                label="Trigger type"
                data={TRIGGER_TYPES.map((t) => ({ value: t, label: t }))}
                value={form.triggerType}
                onChange={(v) =>
                  setForm({ ...form, triggerType: (v as (typeof TRIGGER_TYPES)[number]) ?? 'is' })
                }
              />
              <TextInput
                label="Trigger subject (keyword)"
                value={form.triggerSubject}
                onChange={(e) => setForm({ ...form, triggerSubject: e.currentTarget.value })}
                disabled={form.triggerType === 'welcome'}
              />
            </Group>
            <Select
              label="Reply with"
              data={[
                { value: 'text', label: 'Text message' },
                ...(form.channel === 'whatsapp'
                  ? [{ value: 'flow', label: 'A form (WhatsApp Flow)' }]
                  : []),
              ]}
              value={form.replyKind}
              onChange={(v) => setForm({ ...form, replyKind: (v as 'text' | 'flow') ?? 'text' })}
            />
            {form.replyKind === 'text' ? (
              <Textarea
                label="Reply text"
                minRows={4}
                autosize
                value={form.replyText}
                onChange={(e) => setForm({ ...form, replyText: e.currentTarget.value })}
              />
            ) : (
              <Select
                label="Form to send"
                placeholder="Pick a published form"
                data={(publishedForms.data ?? [])
                  .filter((f) => f.status === 'PUBLISHED')
                  .map((f) => ({ value: f.uid, label: f.name }))}
                value={form.flowUid || null}
                onChange={(v) => setForm({ ...form, flowUid: v ?? '' })}
              />
            )}
            <NumberInput
              label="Priority index (lower = matches first)"
              value={form.priorityIndex}
              onChange={(v) => setForm({ ...form, priorityIndex: Number(v) || 0 })}
            />
            <Group>
              <Button
                leftSection={<IconRobot size={14} />}
                loading={create.isPending || update.isPending}
                onClick={submit}
              >
                {editingId != null ? 'Save changes' : 'Save'}
              </Button>
            </Group>
          </Stack>
        </Modal>

        <Modal
          opened={!!dupSource}
          onClose={() => setDupSource(null)}
          title="Duplicate to other channels"
          centered
        >
          <Stack gap="sm">
            <Text size="sm" c="dimmed">
              Copy <b>{dupSource?.name}</b> from {channelLabel(dupSource?.channel ?? '')} to:
            </Text>
            {CHANNELS.filter((c) => c.value !== dupSource?.channel).map((c) => (
              <Checkbox
                key={c.value}
                label={c.label}
                checked={dupChannels.includes(c.value)}
                onChange={(e) =>
                  setDupChannels((prev) =>
                    e.currentTarget.checked
                      ? [...prev, c.value]
                      : prev.filter((v) => v !== c.value),
                  )
                }
              />
            ))}
            {dupSource?.messageType === 'flow' && (
              <Text size="xs" c="dimmed">
                This is a WhatsApp form reply — on Messenger/Instagram it will be copied as a plain
                reply you can then edit.
              </Text>
            )}
            <Group justify="end">
              <Button variant="default" onClick={() => setDupSource(null)}>
                Cancel
              </Button>
              <Button
                loading={duplicate.isPending}
                disabled={dupChannels.length === 0}
                onClick={() =>
                  dupSource && duplicate.mutate({ id: dupSource.id, toChannels: dupChannels })
                }
              >
                Duplicate
              </Button>
            </Group>
          </Stack>
        </Modal>
      </Stack>
    </motion.div>
  );
}

function BotRepliesRoute() {
  return (
    <PermissionGate permission="manage_bots">
      <BotRepliesPage />
    </PermissionGate>
  );
}

export const Route = createFileRoute('/vendor/bots/replies/')({ component: BotRepliesRoute });
